<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import ExtraForm from './ExtraForm.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmation = ref(false);
const selectedExtra = ref({});
const isSubmitting = ref(false);

const records = computed(
  () => getters['motorReservaExtras/getRecords'].value
);
const uiFlags = computed(
  () => getters['motorReservaExtras/getUIFlags'].value
);

const tableHeaders = computed(() => [
  t('MOTOR_RESERVA.EXTRAS.TABLE.NAME'),
  t('MOTOR_RESERVA.EXTRAS.TABLE.PRICE'),
  t('MOTOR_RESERVA.EXTRAS.TABLE.PRICE_TYPE'),
  t('MOTOR_RESERVA.EXTRAS.TABLE.STATUS'),
  t('MOTOR_RESERVA.EXTRAS.TABLE.ACTIONS'),
]);

const priceTypeLabel = type => {
  const labels = {
    per_night: t('MOTOR_RESERVA.EXTRAS.PRICE_TYPES.PER_NIGHT'),
    per_stay: t('MOTOR_RESERVA.EXTRAS.PRICE_TYPES.PER_STAY'),
    per_person: t('MOTOR_RESERVA.EXTRAS.PRICE_TYPES.PER_PERSON'),
  };
  return labels[type] || type;
};

const formatCurrency = value => {
  return Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const createExtra = async formData => {
  isSubmitting.value = true;
  try {
    await store.dispatch('motorReservaExtras/create', formData);
    useAlert(t('MOTOR_RESERVA.EXTRAS.ADD.API.SUCCESS_MESSAGE'));
    showAddPopup.value = false;
  } catch (error) {
    useAlert(
      error?.message || t('MOTOR_RESERVA.EXTRAS.ADD.API.ERROR_MESSAGE')
    );
  } finally {
    isSubmitting.value = false;
  }
};

const updateExtra = async formData => {
  isSubmitting.value = true;
  try {
    await store.dispatch('motorReservaExtras/update', {
      id: selectedExtra.value.id,
      ...formData,
    });
    useAlert(t('MOTOR_RESERVA.EXTRAS.EDIT.API.SUCCESS_MESSAGE'));
    showEditPopup.value = false;
  } catch (error) {
    useAlert(
      error?.message || t('MOTOR_RESERVA.EXTRAS.EDIT.API.ERROR_MESSAGE')
    );
  } finally {
    isSubmitting.value = false;
  }
};

const deleteExtra = async () => {
  try {
    await store.dispatch(
      'motorReservaExtras/delete',
      selectedExtra.value.id
    );
    useAlert(t('MOTOR_RESERVA.EXTRAS.DELETE.API.SUCCESS_MESSAGE'));
    showDeleteConfirmation.value = false;
  } catch (error) {
    useAlert(
      error?.message || t('MOTOR_RESERVA.EXTRAS.DELETE.API.ERROR_MESSAGE')
    );
  }
};

const openEdit = extra => {
  selectedExtra.value = extra;
  showEditPopup.value = true;
};

const openDelete = extra => {
  selectedExtra.value = extra;
  showDeleteConfirmation.value = true;
};

onBeforeMount(() => {
  store.dispatch('motorReservaExtras/get');
});
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-base font-medium text-slate-800 dark:text-slate-100">
          {{ t('MOTOR_RESERVA.EXTRAS.HEADER') }}
        </h3>
        <p class="text-sm text-slate-600 dark:text-slate-300">
          {{ t('MOTOR_RESERVA.EXTRAS.DESCRIPTION') }}
        </p>
      </div>
      <Button :label="'+ ' + t('MOTOR_RESERVA.EXTRAS.ADD.TITLE')" @click="showAddPopup = true" />
    </div>

    <BaseTable :headers="tableHeaders" :items="records" :loading="uiFlags.isFetching">
      <template #row="{ items }">
        <BaseTableRow v-for="extra in items" :key="extra.id">
        <BaseTableCell>{{ extra.name }}</BaseTableCell>
        <BaseTableCell>{{ formatCurrency(extra.price) }}</BaseTableCell>
        <BaseTableCell>{{ priceTypeLabel(extra.price_type) }}</BaseTableCell>
        <BaseTableCell>
          <span
            class="px-2 py-0.5 text-xs rounded-full"
            :class="
              extra.active
                ? 'bg-green-100 text-green-700'
                : 'bg-slate-100 text-slate-500'
            "
          >
            {{ extra.active ? t('MOTOR_RESERVA.COMMON.ACTIVE') : t('MOTOR_RESERVA.COMMON.INACTIVE') }}
          </span>
        </BaseTableCell>
        <BaseTableCell>
          <div class="flex gap-2">
            <button
              class="text-sm text-woot-500 hover:text-woot-700"
              @click="openEdit(extra)"
            >
              {{ t('MOTOR_RESERVA.COMMON.EDIT') }}
            </button>
            <button
              class="text-sm text-red-500 hover:text-red-700"
              @click="openDelete(extra)"
            >
              {{ t('MOTOR_RESERVA.COMMON.DELETE') }}
            </button>
          </div>
        </BaseTableCell>
        </BaseTableRow>
      </template>
    </BaseTable>

    <!-- Add Modal -->
    <div
      v-if="showAddPopup"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div class="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-lg">
        <h3 class="text-lg font-medium mb-4 text-slate-800 dark:text-slate-100">
          {{ t('MOTOR_RESERVA.EXTRAS.ADD.TITLE') }}
        </h3>
        <ExtraForm
          :is-submitting="isSubmitting"
          @submit="createExtra"
          @cancel="showAddPopup = false"
        />
      </div>
    </div>

    <!-- Edit Modal -->
    <div
      v-if="showEditPopup"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div class="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-lg">
        <h3 class="text-lg font-medium mb-4 text-slate-800 dark:text-slate-100">
          {{ t('MOTOR_RESERVA.EXTRAS.EDIT.TITLE') }}
        </h3>
        <ExtraForm
          :extra="selectedExtra"
          :is-submitting="isSubmitting"
          @submit="updateExtra"
          @cancel="showEditPopup = false"
        />
      </div>
    </div>

    <!-- Delete Confirmation -->
    <div
      v-if="showDeleteConfirmation"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div class="bg-white dark:bg-slate-800 rounded-xl p-6 w-full max-w-sm">
        <p class="text-sm text-slate-700 dark:text-slate-200 mb-4">
          {{ t('MOTOR_RESERVA.EXTRAS.DELETE.CONFIRM') }}
        </p>
        <div class="flex gap-2 justify-end">
          <Button
            variant="secondary"
            :label="t('MOTOR_RESERVA.COMMON.CANCEL')"
            @click="showDeleteConfirmation = false"
          />
          <Button variant="danger" :label="t('MOTOR_RESERVA.COMMON.DELETE')" @click="deleteExtra" />
        </div>
      </div>
    </div>
  </div>
</template>
